import type { PitEntities, Team, TeamsPit } from "../../types";
import { getLoadedChildTeamCount } from "../fetch/orgChartTeamLoad";

export const buildChildTeamsById = (
  childTeams: Team[] = [],
  prevById: Record<string, Team> = {},
  parentId: string | null = null
): Record<string, Team> => {
  const next = { ...prevById };
  childTeams.forEach((stub) => {
    if (!stub?.id) {
      return;
    }
    const directParentId =
      stub.directParentId ?? parentId ?? next[stub.id]?.directParentId;
    next[stub.id] = {
      ...(next[stub.id] || {}),
      ...stub,
      ...(directParentId !== undefined ? { directParentId } : {}),
    };
  });
  return next;
};

export const childListedUnderParent = (
  parent: Team | null | undefined,
  teamId: string
): boolean =>
  Boolean(
    teamId &&
    ((parent?.childTeams || []).some((child) => child?.id === teamId) ||
      parent?.childTeamsById?.[teamId])
  );

export const isTeamRemovedOrHidden = (team: Team | null | undefined): boolean =>
  Boolean(team?.isHidden || team?.isDeleted || team?.isRemoved);

/** A pit row that has been fetched (named or with a loaded roster), not a bare stub. */
export const isLoadedPitRow = (row: Team | null | undefined): boolean =>
  Boolean(
    row &&
    (row.name ||
      Boolean(row.directMemberAggregateIds?.length) ||
      getLoadedChildTeamCount(row) > 0)
  );

/** View root or child still on a parent roster in either pit. */
export const isTeamOnParentRoster = (
  team: Team | null | undefined,
  teamsBaseline: TeamsPit | null | undefined,
  viewRootTeamId: string | undefined,
  teamsDiffline: TeamsPit | null = null
): boolean => {
  if (!team?.id) {
    return false;
  }
  if (team.id === viewRootTeamId) {
    return true;
  }
  const parentId = team.directParentId;
  if (!parentId) {
    return false;
  }
  return (
    childListedUnderParent(teamsBaseline?.[parentId], team.id) ||
    childListedUnderParent(teamsDiffline?.[parentId], team.id)
  );
};

/** Normal view: active team on a parent roster (or view root). */
export const isTeamListedOnGraph = (
  team: Team | null | undefined,
  teamsBaseline: TeamsPit | null | undefined,
  viewRootTeamId: string | undefined,
  teamsDiffline: TeamsPit | null = null
): boolean => {
  if (
    !isTeamOnParentRoster(team, teamsBaseline, viewRootTeamId, teamsDiffline)
  ) {
    return false;
  }
  return !isTeamRemovedOrHidden(team);
};

/**
 * Look up a team by id in a pit: top-level row first, else any parent roster stub.
 */
export const findTeamInPit = (
  pitTeams: TeamsPit | null | undefined,
  teamId: string | null | undefined
): Team | null => {
  if (!pitTeams || !teamId) {
    return null;
  }
  const row = pitTeams[teamId];
  if (row) {
    return row;
  }
  for (const parent of Object.values(pitTeams)) {
    const stub = parent?.childTeamsById?.[teamId];
    if (stub) {
      return stub;
    }
  }
  return null;
};

/**
 * Team row for graph/diff: expanded loads use pit[key]; collection children use parent.childTeamsById.
 */
export const resolveTeamOnPit = (
  pitTeams: TeamsPit,
  teamId: string,
  {
    parentId = null,
    isExpanded = false,
  }: { parentId?: string | null; isExpanded?: boolean } = {}
): Team | null => {
  if (!teamId) {
    return null;
  }
  const pitRow = pitTeams[teamId];
  const rosterStub =
    parentId && pitTeams[parentId]?.childTeamsById?.[teamId]
      ? pitTeams[parentId].childTeamsById![teamId]
      : null;

  if (isExpanded && isLoadedPitRow(pitRow)) {
    return pitRow ?? null;
  }

  if (isLoadedPitRow(pitRow)) {
    return rosterStub ? { ...rosterStub, ...pitRow! } : (pitRow ?? null);
  }

  if (rosterStub) {
    return rosterStub;
  }

  return pitRow ?? null;
};

/** Push a patch onto every parent's roster stub copy of a child team — the
 * owner-pill fast path, so badges update before the full reloads land. */
export const syncChildTeamStubOnParents = (
  pitTeams: TeamsPit,
  teamId: string,
  patch: Partial<Team> = {}
): TeamsPit => {
  if (!teamId || !pitTeams || !Object.keys(patch).length) {
    return pitTeams;
  }

  let changed = false;
  const next: TeamsPit = { ...pitTeams };

  for (const parentId of Object.keys(next)) {
    const parent = next[parentId];
    const stub = parent?.childTeamsById?.[teamId];
    if (!stub) {
      continue;
    }

    const childTeamsById = {
      ...parent.childTeamsById,
      [teamId]: { ...stub, ...patch },
    };
    const childTeams = (parent.childTeams ?? []).map((child) =>
      child?.id === teamId ? { ...child, ...patch } : child
    );

    next[parentId] = { ...parent, childTeamsById, childTeams };
    changed = true;
  }

  return changed ? next : pitTeams;
};

/**
 * Expanded team not yet a pit key — hydrate from an expanded parent's child roster.
 */
export const resolveExpandedTeamFromParentPits = (
  baseline: TeamsPit,
  diffline: TeamsPit | null,
  expandedTeamId: string,
  expandedNodes: Set<string>,
  viewRootTeamId: string
): Team | null => {
  if (!expandedTeamId || expandedTeamId === viewRootTeamId) {
    return null;
  }

  const isParentExpanded = (parentId: string) =>
    parentId === viewRootTeamId || expandedNodes.has(parentId);

  for (const parent of Object.values(baseline)) {
    if (!parent?.id || !isParentExpanded(parent.id)) {
      continue;
    }
    if (!childListedUnderParent(parent, expandedTeamId)) {
      continue;
    }

    const diffParent = diffline?.[parent.id];
    const baseStub = resolveChildTeamOnPit(baseline, parent.id, expandedTeamId);
    const diffStub = diffParent
      ? resolveChildTeamOnPit(diffline, parent.id, expandedTeamId)
      : null;

    return {
      ...baseStub,
      ...diffStub,
      ...baseline[expandedTeamId],
      id: expandedTeamId,
      directParentId: parent.id,
    };
  }

  return baseline[expandedTeamId] ?? null;
};

/**
 * Stub row for a team not yet keyed in the pit map (e.g. first expand fetch).
 * `pitEntities` is the registry for the SAME pit as `pitTeams`; its team copy is
 * the fallback so a team known only via the counterpart fetch (e.g. a removed
 * child off this pit's roster) keeps its state while its expand fetch is in
 * flight, instead of flashing active.
 */
export const resolvePitTeamStubFromParents = (
  pitTeams: TeamsPit | null | undefined,
  teamId: string,
  pitEntities: PitEntities | null = null
): Team | null => {
  if (!teamId || !pitTeams) {
    return null;
  }
  if (pitTeams[teamId]) {
    return pitTeams[teamId];
  }
  for (const parent of Object.values(pitTeams)) {
    const fromById = parent?.childTeamsById?.[teamId];
    if (fromById) {
      return fromById;
    }
    const fromList = parent?.childTeams?.find((stub) => stub?.id === teamId);
    if (fromList) {
      return fromList;
    }
  }
  return pitEntities?.teamsById?.[teamId] ?? null;
};

export const resolveChildTeamOnPit = (
  pitTeams: TeamsPit | null | undefined,
  parentId: string,
  childId: string
): Team | null => {
  if (!pitTeams) {
    return null;
  }
  const pitRow = pitTeams[childId];
  if (pitRow && isLoadedPitRow(pitRow)) {
    return pitRow;
  }
  return (
    pitTeams[parentId]?.childTeamsById?.[childId] ??
    pitTeams[parentId]?.childTeams?.find((stub) => stub?.id === childId) ??
    null
  );
};
